import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { FeedStamp } from './FeedStamp.js';
import {
  assignEmployeeColors,
  metricColor,
  chipLabel,
  computeActivityCoverage,
  computeActivityPresence,
  computeDayWorkedHours,
  businessDayFetchRange,
  frDayLabel,
  aBlancEquivalents,
  buildRegistry,
  checkInTime,
  draftBoardDay,
  evaluateMetric,
  formatMetricValue,
  dayForecasts,
  nextOccupancy,
  slotsOverlappingWindow,
  profileOutputsForDay,
  staffingConfigFromRows,
  summaryMetrics,
  workloadFromCounts,
  workloadFromDay,
  type DayForecast,
  type MetricDef,
  type StaffingConfigSnapshot,
  sortActivitiesByStart,
  teamsInActivities,
  todayIsoParis,
  type ActivityOverride,
  type ActivityPresence,
  type ActivityQualification,
  type BoardAbsence,
  type BoardActivity,
  type BoardDay,
  type DayCoverage,
  type DayPresence,
  type Employee,
} from '@lemontaigu/staff';
import {
  useBoard,
  useBoardDemand,
  useBoardPresence,
  useBoardRealtime,
  useMetricRegistry,
  useStaffingConfig,
  useTimesheet,
} from '@lemontaigu/staff/react';
import { useCapability } from '@lemontaigu/platform-ui';
import { CAPABILITIES } from '@lemontaigu/platform';
import { FrDateInput } from '../ui/FrDateInput.js';
import { broadcastGaps } from './broadcast.js';
import { AssignEditor, DeclareEditor } from './coverageEditors.js';
import { EmployeeWeek } from './EmployeeWeek.js';
import { PresenceMatrix } from './PresenceMatrix.js';
import { EmployeeQuickEdit } from './EmployeeQuickEdit.js';
import {
  dayRange,
  demandDraftError,
  demandDraftFromDay,
  demandNum,
  demandParams,
  fmtShortDayLabel,
  isPlannedDay,
  punchWindowOf,
  shiftDay,
  teamLabel,
  weekDays,
  weekStartKey,
  DEMAND_HOTEL_FIELDS,
  DEMAND_RESTAURANT_FIELDS,
  FEED_HINT,
  coversOf,
  type DemandDraft,
} from './logic.js';

// The « Semaine » view (S4 projections): weekly load input (one grid, the
// whole week in a single gesture) + the Gantt-like board — activities as
// rows, days as columns, each cell colour-coded with the covering first
// names and the driver's load, plus a per-day « Diffuser » broadcast of the
// positions to fill. Days with neither load nor declarations render as
// neutral « non planifié » (a wall of red on every empty future day would
// be noise, not information — isPlannedDay). Rows are CHRONOLOGICAL by
// window start (2026-07-19, matching the Day Gantt reorder), team conveyed
// by a coloured left bar + coloured label font plus a legend, instead of the
// old team-grouped sections.

/** Team → the CSS class carrying its --team-fg colour (null = the neutral « Autres »). */
const teamClass = (team: string | null): string => `team-${team ?? 'none'}`;

// The PostgREST column lists and the row mappers that used to live here moved
// into the BoardStore adapter at ladder step 6 (they were duplicated verbatim
// across four views). What stays is the view's own concern: bucketing
// already-mapped domain objects by day.
const presenceMapOf = (rows: ActivityPresence[]): Map<string, ActivityPresence[]> => {
  const map = new Map<string, ActivityPresence[]>();
  for (const p of rows) map.set(p.date, [...(map.get(p.date) ?? []), p]);
  return map;
};
const absenceMapOf = (rows: BoardAbsence[]): Map<string, Set<string>> => {
  const map = new Map<string, Set<string>>();
  for (const r of rows) {
    const set = map.get(r.date) ?? new Set<string>();
    set.add(r.employeeId);
    map.set(r.date, set);
  }
  return map;
};

export function WeekView({
  activities,
  qualifications,
  employees,
  canAssign,
  showHours = false,
}: {
  activities: BoardActivity[];
  qualifications: ActivityQualification[];
  employees: Employee[];
  /** bos@ (is_admin) → ★ assign; reception@ → declared only (0045). Editing wired next phase. */
  canAssign: boolean;
  /** Présence matrix: also fetch + show each day's actual worked hours per employee (2026-08-04, bos@ only). */
  showHours?: boolean;
}) {
  const board = useBoard();
  const boardPresence = useBoardPresence();
  const boardDemand = useBoardDemand();
  const boardRealtime = useBoardRealtime();
  const staffingConfig = useStaffingConfig();
  const metricRegistry = useMetricRegistry();
  const timesheet = useTimesheet();
  // Applying a forecast writes a per-day override — same gate as the ✎ editor.
  const canWriteBoard = useCapability(CAPABILITIES.RH_BOARD_WRITE);
  // Home week = the one containing TODAY (operator, 2026-07-20 — matches the
  // Jour tab). Anchoring on tomorrow also skipped a whole week every Sunday.
  const [weekStart, setWeekStart] = useState(() => weekStartKey(todayIsoParis()));
  const days = useMemo(() => weekDays(weekStart), [weekStart]);

  const [boardDays, setBoardDays] = useState<Map<string, BoardDay>>(new Map());
  const [overrides, setOverrides] = useState<OverrideByDay>(new Map());
  const [presencesByDay, setPresencesByDay] = useState<Map<string, ActivityPresence[]>>(new Map());
  const [absencesByDay, setAbsencesByDay] = useState<Map<string, Set<string>>>(new Map());
  // Punches per day (0046) — feeds the ⚡ « d'après le pointage » candidates in
  // the week cell editor, day-scoped like the Jour view.
  const [clockByDay, setClockByDay] = useState<Map<string, { clockIn: string; clockOut: string | null; employeeId: string }[]>>(new Map());
  const [loaded, setLoaded] = useState(false);
  const [err, setErr] = useState('');

  const [drafts, setDrafts] = useState<Map<string, DemandDraft>>(new Map());
  const [dirty, setDirty] = useState<Set<string>>(new Set());
  // Read by the realtime handler. A ref, not the state itself: putting `dirty`
  // in that effect's deps would tear down and re-subscribe the channel on every
  // keystroke.
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  // Occupancy of the night before the week — seeds the running chain (0041).
  const [seedOccupancy, setSeedOccupancy] = useState<number | null>(null);
  // Staffing profiles for the « prévisionnel » strip; loaded once, never fatal.
  const [staffingSnapshot, setStaffingSnapshot] = useState<StaffingConfigSnapshot | null>(null);
  const [metrics, setMetrics] = useState<MetricDef[]>([]);
  /** `${date}:${activityId}` currently being written, for the busy state. */
  const [applying, setApplying] = useState<string | null>(null);
  /** A realtime change arrived while the grid had unsaved edits — offered, not applied. */
  const [staleFromElsewhere, setStaleFromElsewhere] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [sentDays, setSentDays] = useState<Set<string>>(new Set());
  const [focusEmployee, setFocusEmployee] = useState<{ employee: Employee; day: string } | null>(null);
  // Which (activity, day) cell is being edited (0045) — the same assign/declare
  // editors as the Jour view, opened per week cell.
  const [editing, setEditing] = useState<{ activityId: number; day: string } | null>(null);
  const [hoursByDay, setHoursByDay] = useState<Map<string, Map<string, number>> | undefined>(undefined);
  // Présence-matrix cell quick-edit (2026-08-04) — distinct from `focusEmployee`
  // above, which opens the read-only EmployeeWeek from a Gantt chip.
  const [quickEdit, setQuickEdit] = useState<{ employee: Employee; day: string } | null>(null);

  const load = useCallback(
    async (start: string) => {
      setErr('');
      setLoaded(false);
      const end = shiftDay(start, 6);
      // UTC-anchored superset covering the 04:00 business-day overhang (end is
      // EXCLUSIVE here) — computeDayWorkedHours buckets exactly and the matrix
      // reads only the visible days' keys.
      const { fromIso: hoursFromIso, toIso: hoursToIso } = businessDayFetchRange(start, shiftDay(end, 1));
      try {
        const [d, o, p, ab, seed, t, h] = await Promise.all([
          board.days(start, end),
          board.overrides(start, end),
          board.presence(start, end),
          board.absences(start, end),
          board.occupancyOn(shiftDay(start, -1)),
          // The punch read is C3's TIMESHEET aggregate, not the board's — its
          // own port. clock_in is timestamptz, so the bounds are instants.
          timesheet.entriesBetween(dayRange(start).fromIso, dayRange(end).toIso),
          // Worked hours for the Présence matrix (bos@ only). A SECOND read
          // rather than a reuse of `t` above: `t` is bounded by calendar days
          // and bucketed by calendar day for the punch strips, whereas
          // computeDayWorkedHours buckets by BUSINESS day — a 02:00 clock-in
          // belongs to the previous one. Reusing `t` would under-count any
          // night shift crossing midnight at the week's end.
          showHours ? timesheet.entriesBetween(hoursFromIso, hoursToIso) : Promise.resolve([]),
        ]);
        const dayMap = new Map<string, BoardDay>(d.map((day) => [day.date, day]));
        const overrideMap: OverrideByDay = new Map();
        for (const ov of o) overrideMap.set(ov.date, [...(overrideMap.get(ov.date) ?? []), ov]);
        const clockMap = new Map<string, { clockIn: string; clockOut: string | null; employeeId: string }[]>();
        for (const r of t) {
          const key = todayIsoParis(new Date(r.clockIn));
          clockMap.set(key, [...(clockMap.get(key) ?? []), { employeeId: r.employeeId, clockIn: r.clockIn, clockOut: r.clockOut }]);
        }
        setBoardDays(dayMap);
        setOverrides(overrideMap);
        setPresencesByDay(presenceMapOf(p));
        setAbsencesByDay(absenceMapOf(ab));
        setClockByDay(clockMap);
        setHoursByDay(showHours ? computeDayWorkedHours(h) : undefined);
        setSeedOccupancy(seed);
        const nextDrafts = new Map<string, DemandDraft>();
        for (const key of weekDays(start)) nextDrafts.set(key, demandDraftFromDay(dayMap.get(key) ?? null));
        setDrafts(nextDrafts);
        setDirty(new Set());
        setSaved(false);
        setSentDays(new Set());
        setStaleFromElsewhere(false);
        setLoaded(true);
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      }
    },
    [board, timesheet, showHours],
  );

  useEffect(() => {
    void load(weekStart);
  }, [weekStart, load]);

  /**
   * Realtime (2026-08-01): the week view used to recompute only on navigation
   * or save, so a booking landing while it was open left the prévisionnel
   * stale with nothing saying so. The Jour view has been subscribed all along.
   *
   * The catch is that `load()` REPLACES the drafts, so auto-reloading while the
   * desk is mid-entry would silently discard typed figures. So: reload
   * immediately when nothing is dirty, and when something is, hold the refresh
   * and offer it — the desk's unsaved work outranks freshness, and being told
   * beats being overwritten.
   */
  useEffect(() => {
    return boardRealtime.watchBoard(() => {
      if (dirtyRef.current.size > 0) setStaleFromElsewhere(true);
      else void load(weekStart);
    });
  }, [boardRealtime, load, weekStart]);

  // Staffing config is CONFIGURATION, not week data: loaded once, and a failure
  // only costs the prévisionnel strip — never the board.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const snap = await staffingConfig.load();
        if (!cancelled) setStaffingSnapshot(snap);
      } catch {
        if (!cancelled) setStaffingSnapshot(null);
      }
      try {
        const defs = await metricRegistry.load();
        if (!cancelled) setMetrics(defs);
      } catch {
        // A badge falling back to the raw demand number is a worse display,
        // never a broken week.
        if (!cancelled) setMetrics([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [staffingConfig, metricRegistry]);

  const employeesById = useMemo(() => new Map(employees.map((e) => [e.id, e])), [employees]);
  const currentIds = useMemo(() => employees.map((e) => e.id), [employees]);

  const specs = useMemo(
    () =>
      staffingSnapshot
        ? staffingConfigFromRows({
            profiles: staffingSnapshot.profiles,
            bands: staffingSnapshot.bands,
            posts: staffingSnapshot.posts,
            workContent: staffingSnapshot.workContent,
          }).specs
        : [],
    [staffingSnapshot],
  );

  const registry = useMemo(() => buildRegistry(metrics), [metrics]);

  /**
   * A metric context PER DAY. The week is not one context repeated: a
   * profile's published outputs (à-blanc equivalents, heads) are computed from
   * that day's own room workload, so sharing one context across seven days
   * would badge every day with Monday's figure.
   */
  const metricContexts = useMemo(() => {
    const map = new Map<string, { day: BoardDay | null; profileOutputs?: ReturnType<typeof profileOutputsForDay> }>();
    for (const key of days) {
      const bd = boardDays.get(key) ?? null;
      map.set(key, {
        day: bd,
        profileOutputs: bd
          ? profileOutputsForDay({ specs, day: bd, workload: workloadFromDay(bd), readyBy: checkInTime(bd.arrivals) })
          : undefined,
      });
    }
    return map;
  }, [days, boardDays, specs]);

  const coverages = useMemo(() => {
    const map = new Map<string, DayCoverage>();
    for (const key of days) {
      map.set(
        key,
        computeActivityCoverage({
          date: key,
          activities,
          overrides: overrides.get(key) ?? [],
          day: boardDays.get(key) ?? null,
          presences: presencesByDay.get(key) ?? [],
          currentEmployeeIds: currentIds,
          absentEmployeeIds: absencesByDay.get(key) ?? [],
          metrics: registry,
          metricContext: metricContexts.get(key),
        }),
      );
    }
    return map;
  }, [days, activities, overrides, boardDays, presencesByDay, absencesByDay, currentIds, registry, metricContexts]);

  // The « Présence de la semaine » matrix (operator request 2026-08-04): the
  // same table staff@ Planning/Semaine and the Mois view render — days ×
  // employees-grouped-by-team, each cell the declared thirds or a full-red
  // Absent·e — so the desk reads the week's repos without switching horizon.
  // `activities` is already the timed subset here (Planning filters « sans
  // horaire » out before mounting this view), so no further filtering.
  const presenceByDay = useMemo(() => {
    const map = new Map<string, Map<string, DayPresence>>();
    for (const key of days) {
      map.set(
        key,
        computeActivityPresence({
          date: key,
          activities,
          overrides: overrides.get(key) ?? [],
          presences: presencesByDay.get(key) ?? [],
          absentEmployeeIds: absencesByDay.get(key) ?? [],
          currentEmployeeIds: currentIds,
        }),
      );
    }
    return map;
  }, [days, activities, overrides, presencesByDay, absencesByDay, currentIds]);

  const planned = (key: string) =>
    isPlannedDay(boardDays.has(key), (presencesByDay.get(key) ?? []).length, 0);

  // Occupancy across the week (algorithm locked 2026-08-01 —
  // docs/OCCUPANCY-CHAIN.md). Per the operator's rule, an input on day d
  // recalculates Recouches for d and then Nr/No for every FOLLOWING day.
  //
  //   * a day the desk has edited is an ANCHOR — its typed Occupation stands,
  //     and Recouches = No − Na;
  //   * every day after the first edited one is CHAINED, discarding whatever
  //     occupancy was stored (it is a consequence, not a fact);
  //   * days before the first edit keep their stored figures untouched.
  const occByDay = useMemo(() => {
    const map = new Map<string, { occupancy: number | null; recouches: number | null }>();
    let running = seedOccupancy;
    let chaining = false; // flips at the first edited day
    for (const key of days) {
      const draft = drafts.get(key);
      const arr = demandNum(draft?.arrivals ?? '');
      const dep = demandNum(draft?.departures ?? '');
      const typed = (draft?.occupancy ?? '').trim();
      const chained = nextOccupancy(running, arr, dep);
      const isDirty = dirty.has(key);
      let occupancy: number | null;
      if (isDirty && typed !== '') {
        occupancy = Number(typed); // anchor: the desk's figure for this day
      } else if (chaining) {
        occupancy = chained; // consequence of an earlier edit
      } else {
        occupancy = boardDays.get(key)?.occupancy ?? chained;
      }
      if (isDirty) chaining = true;
      map.set(key, { occupancy, recouches: occupancy != null ? Math.max(0, occupancy - arr) : null });
      running = occupancy;
    }
    return map;
  }, [days, drafts, boardDays, seedOccupancy, dirty]);

  // ── « Prévisionnel » across the week (2026-08-01) ─────────────────────────
  /** Per-day forecasts. Each day gets its OWN window — they are not one window repeated. */
  const summaryDefs = useMemo(() => summaryMetrics(registry, { derivedOnly: true }), [registry]);

  /**
   * The derived rows — one per flagged metric, evaluated against the LIVE draft
   * counts so a typed departure moves them immediately (ADR-0015 « afficher en
   * résumé »). Replaces the single hard-coded « Charge » row.
   *
   * `derivedOnly`: Départs, Arrivées and the rest are the inputs directly above,
   * so repeating one as a derived row underneath would be noise.
   */
  const summaryByDay = useMemo(() => {
    const map = new Map<string, Map<string, string | null>>();
    if (summaryDefs.length === 0) return map;
    for (const key of days) {
      const draft = drafts.get(key);
      if (!draft) continue;
      const day = draftBoardDay({
        date: key,
        departures: demandNum(draft.departures),
        arrivals: demandNum(draft.arrivals),
        occupancy: occByDay.get(key)?.occupancy ?? null,
        breakfasts: demandNum(draft.breakfasts),
        halfBoards: demandNum(draft.halfBoards),
        diners: demandNum(draft.diners),
      });
      const ctx = {
        day,
        profileOutputs: profileOutputsForDay({
          specs,
          day,
          workload: workloadFromDay(day),
          readyBy: checkInTime(day.arrivals),
        }),
      };
      const memo = new Map<string, number | null>();
      const row = new Map<string, string | null>();
      for (const def of summaryDefs) {
        // The number alone, as in the Jour row (2026-09-02): seven columns of
        // « 26,1 à-blancs éq. » is the unit repeated seven times beside a row
        // heading that already names the metric. The Mois cell keeps its
        // suffix — it shows no headings.
        const value = evaluateMetric(registry, def.key, ctx, memo).value;
        row.set(def.key, value === null ? null : formatMetricValue(value, def.unit));
      }
      map.set(key, row);
    }
    return map;
  }, [summaryDefs, days, drafts, occByDay, specs, registry]);

  const weekForecasts = useMemo(() => {
    const map = new Map<string, DayForecast[]>();
    if (specs.length === 0) return map;
    for (const key of days) {
      const bd = boardDays.get(key);
      if (!bd) continue;
      const rows = dayForecasts({
        specs,
        day: bd,
        workload: workloadFromDay(bd),
        readyBy: checkInTime(bd.arrivals),
      });
      if (rows.length > 0) map.set(key, rows);
    }
    return map;
  }, [specs, days, boardDays]);


  /** The distinct activities with a forecast anywhere in the week — the strip's rows. */
  const forecastActivities = useMemo(() => {
    const seen = new Map<number, string>();
    for (const rows of weekForecasts.values()) {
      for (const f of rows) if (!seen.has(f.activityId)) seen.set(f.activityId, f.label);
    }
    return [...seen].map(([id, label]) => ({ id, label }));
  }, [weekForecasts]);

  /**
   * Days whose planned window was deliberately set and no longer matches what
   * the load now implies — the week-level answer to « nothing tells you Tuesday
   * drifted while you were looking at Monday ».
   *
   * Counts DAYS, not (day, activity) pairs: the desk navigates by day, so the
   * actionable number is how many days need revisiting.
   */
  const driftedDays = useMemo(() => {
    const out: string[] = [];
    for (const [key, rows] of weekForecasts) {
      const overridesHere = overrides.get(key) ?? [];
      const drifted = rows.some((f) => {
        if (!overridesHere.some((o) => o.activityId === f.activityId)) return false;
        const c = coverages.get(key)?.activities.find((x) => x.activity.id === f.activityId);
        if (!c) return false;
        return c.windowStart.slice(0, 5) !== f.windowStart || c.windowEnd.slice(0, 5) !== f.windowEnd;
      });
      if (drifted) out.push(key);
    }
    return out;
  }, [weekForecasts, overrides, coverages]);

  /** Effective planned window for (day, activity) — override ⊕ default, as the Gantt draws it. */
  const plannedWindowFor = useCallback(
    (key: string, activityId: number) => {
      const c = coverages.get(key)?.activities.find((x) => x.activity.id === activityId);
      return c ? { windowStart: c.windowStart, windowEnd: c.windowEnd } : null;
    },
    [coverages],
  );

  /** Align ONE day's planned window to its forecast — a per-day override. */
  const applyForecast = async (key: string, f: DayForecast) => {
    setApplying(`${key}:${f.activityId}`);
    setErr('');
    const existing = (overrides.get(key) ?? []).find((o) => o.activityId === f.activityId);
    try {
      await boardDemand.saveOverride({
        activityId: f.activityId,
        date: key,
        windowStart: f.windowStart,
        windowEnd: f.windowEnd,
        required: existing?.required ?? true,
        note: existing?.note ?? null,
      });
      await load(weekStart);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setApplying(null);
    }
  };

  const setDraftField = (key: string, field: keyof DemandDraft, value: string) => {
    const current = drafts.get(key) ?? demandDraftFromDay(null);
    const next = new Map(drafts);
    next.set(key, { ...current, [field]: value });
    setDrafts(next);
    setDirty(new Set(dirty).add(key));
    setSaved(false);
  };

  /**
   * Save the week's DESK values — since M4 that is « Dîners » and the note, and
   * nothing else (§6.1).
   *
   * Two things went with the feed, and both are deliberate:
   *
   *  - `saveDemand` (the whole-row upsert). It carried Départs, Arrivées and
   *    Occupation out of the draft; those are now fed, so writing them back
   *    would push a possibly-stale copy over what the importer just wrote.
   *  - the silent forward propagation of the occupancy chain. `OCCUPANCY-CHAIN`
   *    exists to reconcile hand-entered, over-determined figures, and §6.3
   *    records the operator retiring it: "the chain is not needed anymore since
   *    all of these are inferred from arrivals/departures". With no editable
   *    occupancy there is nothing to propagate FROM. The chain code stays —
   *    after cutover the platform's own PMS feeds the same columns the same way.
   */
  const saveWeek = async () => {
    const rows: { date: string; diners: number; note: string | null }[] = [];
    for (const key of dirty) {
      const draft = drafts.get(key);
      if (!draft) continue;
      const invalid = demandDraftError(draft);
      if (invalid) {
        setErr(`${fmtShortDayLabel(key)} : ${invalid}`);
        return;
      }
      rows.push({
        date: key,
        diners: demandNum(draft.diners),
        note: draft.note.trim() === '' ? null : draft.note,
      });
    }
    if (rows.length === 0) return;
    setBusy(true);
    setErr('');
    try {
      for (const row of rows) await boardDemand.saveDeskDay(row);
      setSaved(true);
      void load(weekStart);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const broadcast = async (key: string) => {
    const gaps = coverages.get(key)?.gaps ?? [];
    if (gaps.length === 0) return;
    const summary = gaps.map((g) => g.activity.label).join(', ');
    if (
      !window.confirm(
        `Diffuser les postes à pourvoir ${frDayLabel(key)} (${summary}) dans le canal Staff ? Tous les téléphones recevront une notification.`,
      )
    )
      return;
    setBusy(true);
    setErr('');
    try {
      await broadcastGaps(key, gaps);
      setSentDays(new Set(sentDays).add(key));
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  // Coverage-only reload after an edit — does NOT touch drafts/dirty (unsaved
  // demand edits survive) or close the editor panel, so the desk can edit
  // several cells in a row (the DayView reloadAssignments reasoning).
  const reloadCoverage = useCallback(
    async (start: string) => {
      const end = shiftDay(start, 6);
      try {
        const [p, ab] = await Promise.all([board.presence(start, end), board.absences(start, end)]);
        setPresencesByDay(presenceMapOf(p));
        setAbsencesByDay(absenceMapOf(ab));
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      }
    },
    [board],
  );

  const cellWindow = (activityId: number, day: string): { start: string; end: string } => {
    const a = activities.find((x) => x.id === activityId);
    const o = (overrides.get(day) ?? []).find((x) => x.activityId === activityId);
    return {
      start: o?.windowStart ?? a?.windowStart ?? '00:00',
      end: o?.windowEnd ?? a?.windowEnd ?? '00:00',
    };
  };

  // Since step 7 this also lifts the day's absence in the same transaction.
  const upsertPresence = (
    activityId: number,
    day: string,
    employeeId: string,
    status: 'declared' | 'assigned',
    source: 'desk' | 'clock' | 'operator',
    declaredBy: string | null,
  ) => boardPresence.declarePresence({ activityId, employeeId, date: day, status, source, declaredBy });

  const assignEmployee = async (activityId: number, day: string, employeeId: string) => {
    setBusy(true);
    setErr('');
    try {
      await upsertPresence(activityId, day, employeeId, 'assigned', 'operator', null);
      void reloadCoverage(weekStart);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const unassignEmployee = async (activityId: number, day: string, employeeId: string) => {
    setBusy(true);
    setErr('');
    try {
      await boardPresence.withdrawPresence(activityId, employeeId, day);
      void reloadCoverage(weekStart);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  // Declared presence on a week cell (ADR-0011) — ONE activity row, source='desk'.
  const declareOnActivity = async (activityId: number, day: string, employeeId: string, via: string | null) => {
    setBusy(true);
    setErr('');
    try {
      await upsertPresence(activityId, day, employeeId, 'declared', 'desk', via);
      void reloadCoverage(weekStart);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const undeclareOnActivity = async (activityId: number, day: string, employeeId: string) => {
    setBusy(true);
    setErr('');
    try {
      // reception@ RLS only deletes its own desk/clock declared rows; bos@ any.
      await boardPresence.withdrawPresence(activityId, employeeId, day);
      void reloadCoverage(weekStart);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  // Employees with a punch-derived (source='clock') row that day — the ⚡ flag
  // on the grid chips, apart from ★ assigned and a plain declaration.
  const clockIdsForDay = (day: string): Set<string> =>
    new Set((presencesByDay.get(day) ?? []).filter((p) => p.source === 'clock').map((p) => p.employeeId));

  const provenanceFor = (activityId: number, day: string, employeeId: string): string => {
    const p = (presencesByDay.get(day) ?? []).find((x) => x.activityId === activityId && x.employeeId === employeeId);
    if (!p || p.status === 'assigned') return '';
    if (p.source === 'tap') return '📱';
    if (p.source === 'message') return '💬';
    if (p.source === 'clock') return '⚡';
    if (p.source === 'desk' && p.declaredBy) {
      const e = employeesById.get(p.declaredBy);
      return e ? `via ${e.firstName}` : 'via …';
    }
    return '';
  };

  // Per-cell ⚡: attach a present person's shift to this activity/day as a clock row.
  const autoDeclareOnActivity = async (activityId: number, day: string, employeeId: string) => {
    setBusy(true);
    setErr('');
    try {
      await upsertPresence(activityId, day, employeeId, 'declared', 'clock', null);
      void reloadCoverage(weekStart);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  // Present (clocked) qualified staff whose shift overlaps this cell, not yet covering.
  const autoCandidatesFor = (activityId: number, day: string): string[] => {
    const { start, end } = cellWindow(activityId, day);
    const actSlots = new Set(slotsOverlappingWindow(start, end));
    if (actSlots.size === 0) return [];
    const qualified = new Set(qualifications.filter((q) => q.activityId === activityId).map((q) => q.employeeId));
    const cov = coverages.get(day)?.activities.find((x) => x.activity.id === activityId);
    const covering = new Set([...(cov?.assigned ?? []), ...(cov?.coveredBy ?? [])]);
    const byEmp = new Map<string, { clockIn: string; clockOut: string | null }[]>();
    for (const e of clockByDay.get(day) ?? []) byEmp.set(e.employeeId, [...(byEmp.get(e.employeeId) ?? []), e]);
    const out: string[] = [];
    for (const [empId, entries] of byEmp) {
      if (!qualified.has(empId) || covering.has(empId)) continue;
      const w = punchWindowOf(entries);
      if (w && slotsOverlappingWindow(w.start, w.end).some((s) => actSlots.has(s))) out.push(empId);
    }
    return out;
  };

  const chipColors = useMemo(() => assignEmployeeColors(employees.map((e) => e.id)), [employees]);

  const activeActivities = useMemo(() => activities.filter((a) => a.active), [activities]);
  const sortedActivities = useMemo(() => sortActivitiesByStart(activeActivities), [activeActivities]);
  const legendTeams = useMemo(() => teamsInActivities(activeActivities), [activeActivities]);

  const thisWeek = weekStartKey(todayIsoParis());

  return (
    <div className="planning-board planning-week">
      <div className="staff-daybar">
        <button type="button" onClick={() => setWeekStart(shiftDay(weekStart, -7))} aria-label="Semaine précédente">
          ‹
        </button>
        <FrDateInput
          className="pt-datepicker"
          value={weekStart}
          onChange={(v) => v && setWeekStart(weekStartKey(v))}
        />
        <button type="button" onClick={() => setWeekStart(shiftDay(weekStart, 7))} aria-label="Semaine suivante">
          ›
        </button>
        <span className="staff-daylabel">
          Semaine du {frDayLabel(weekStart)} au {frDayLabel(shiftDay(weekStart, 6))}
        </span>
        {weekStart !== thisWeek && (
          <button type="button" className="staff-today" onClick={() => setWeekStart(thisWeek)}>
            Cette semaine
          </button>
        )}
      </div>

      {err && <p className="auth-err">Erreur : {err}</p>}
      {!loaded && !err && <p className="staff-empty">Chargement…</p>}

      {loaded && (
        <>
          <section className="board-demand">
            {/* Same move as the Jour view (2026-09-02): the stamp qualifies
                every figure in the section, so it goes on the title rather than
                on the HÔTEL row. « prévue — saisie de » went with it: after M4
                one cell on this grid is typed. */}
            <h3 className="board-section-title">
              Charge de la semaine
              <FeedStamp />
            </h3>
            <div className="week-scroll">
              <table className="week-demand-table">
                <thead>
                  <tr>
                    <th />
                    {days.map((key) => (
                      <th key={key} className={planned(key) ? '' : 'is-unplanned'}>
                        {fmtShortDayLabel(key)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* Row order locked by the operator 2026-08-01, matching the
                      Jour view: Départs · Arrivées · Occupation · Recouches.
                      M4 (2026-09-01): the first three are WRITTEN BY THE VEGA
                      FEED and are read-only (§6.2) — rendered as plain cells,
                      the same as Recouches, rather than as disabled inputs. */}
                  <tr className="week-demand-group">
                    <th colSpan={days.length + 1}>Hôtel</th>
                  </tr>
                  {DEMAND_HOTEL_FIELDS.map((f) => (
                    <tr
                      key={f.key}
                      className="week-demand-derived is-fed"
                      style={{ '--metric-color': metricColor(f.key) } as CSSProperties}
                      title={FEED_HINT}
                    >
                      <th>{f.label}</th>
                      {days.map((key) => {
                        const v = drafts.get(key)?.[f.key] ?? '';
                        return <td key={key}>{v === '' ? '—' : v}</td>;
                      })}
                    </tr>
                  ))}
                  <tr
                    className="week-demand-derived"
                    style={{ '--metric-color': metricColor('stayovers') } as CSSProperties}
                  >
                    <th>Recouches</th>
                    {days.map((key) => (
                      <td key={key}>{occByDay.get(key)?.recouches ?? '—'}</td>
                    ))}
                  </tr>
                  {summaryDefs.map((def) => (
                    <tr
                      key={def.key}
                      className="week-demand-derived"
                      style={{ '--metric-color': metricColor(def.key) } as CSSProperties}
                    >
                      <th title={def.labelFr}>{def.labelFr}</th>
                      {days.map((key) => (
                        <td key={key}>{summaryByDay.get(key)?.get(def.key) ?? '—'}</td>
                      ))}
                    </tr>
                  ))}

                  <tr className="week-demand-group">
                    <th colSpan={days.length + 1}>Restaurant</th>
                  </tr>
                  {/* « Dîners » is the one row still typed here — walk-ins and
                      à la carte, which no reservation exists for (§6.1). */}
                  {DEMAND_RESTAURANT_FIELDS.map((f) =>
                    f.feedOwned ? (
                      <tr
                        key={f.key}
                        className="week-demand-derived is-fed"
                        style={{ '--metric-color': metricColor(f.key) } as CSSProperties}
                        title={FEED_HINT}
                      >
                        <th>{f.label}</th>
                        {days.map((key) => {
                          const v = drafts.get(key)?.[f.key] ?? '';
                          return <td key={key}>{v === '' ? '—' : v}</td>;
                        })}
                      </tr>
                    ) : (
                      <tr key={f.key} style={{ '--metric-color': metricColor(f.key) } as CSSProperties}>
                        <th>{f.label}</th>
                        {days.map((key) => (
                          <td key={key}>
                            <input
                              type="text"
                              inputMode="numeric"
                              placeholder="0"
                              value={drafts.get(key)?.[f.key] ?? ''}
                              onChange={(e) => setDraftField(key, f.key, e.target.value)}
                            />
                          </td>
                        ))}
                      </tr>
                    ),
                  )}
                  <tr
                    className="week-demand-derived"
                    style={{ '--metric-color': metricColor('covers') } as CSSProperties}
                  >
                    <th>Couverts</th>
                    {days.map((key) => {
                      const d = drafts.get(key);
                      return <td key={key}>{d ? coversOf(d) : '—'}</td>;
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="board-demand-actions">
              <button type="button" disabled={busy || dirty.size === 0} onClick={() => void saveWeek()}>
                Enregistrer la semaine{dirty.size > 0 ? ` (${dirty.size} jour${dirty.size > 1 ? 's' : ''})` : ''}
              </button>
              {saved && <span className="board-saved">Enregistré ✓</span>}
              {/* A change landed elsewhere while this grid had unsaved edits.
                  Offered rather than applied — reloading would discard them. */}
              {staleFromElsewhere && (
                <span className="board-stale">
                  Charge modifiée ailleurs — vos saisies non enregistrées seront perdues.{' '}
                  <button type="button" className="board-stale-reload" onClick={() => void load(weekStart)}>
                    Recharger
                  </button>
                </span>
              )}
            </div>
          </section>

          <PresenceMatrix
            employees={employees}
            days={days}
            presenceByDay={presenceByDay}
            today={todayIsoParis()}
            hoursByDay={hoursByDay}
            title="Présence de la semaine"
            canEditCell={canAssign ? () => true : undefined}
            onCellClick={(employeeId, date) => {
              const employee = employees.find((e) => e.id === employeeId);
              if (employee) setQuickEdit({ employee, day: date });
            }}
          />

          {quickEdit && (
            <EmployeeQuickEdit
              employee={quickEdit.employee}
              color={chipColors.get(quickEdit.employee.id)}
              anchorDay={quickEdit.day}
              activities={activities}
              qualifications={qualifications}
              onClose={() => setQuickEdit(null)}
            />
          )}

          <section>
            <h3 className="board-section-title">Couverture des activités</h3>
            {/* « Prévisionnel » above the Gantt (operator request 2026-08-01),
                per-day: each column carries its OWN derived window — they
                differ day to day, which is the whole point — and its own apply
                control, so the desk aligns the days it wants. */}
            {weekForecasts.size > 0 && (
              <div className="previsionnel previsionnel-week">
                <div className="previsionnel-head">
                  <strong>Prévisionnel</strong>
                  <span className="previsionnel-hint">calculé d’après la charge saisie</span>
                  {driftedDays.length > 0 && (
                    <span
                      className="previsionnel-badge"
                      title={`Horaires fixés puis dépassés par la charge : ${driftedDays
                        .map((k) => fmtShortDayLabel(k))
                        .join(', ')}`}
                    >
                      ⚠ {driftedDays.length} jour{driftedDays.length > 1 ? 's' : ''} à réaligner
                    </span>
                  )}
                </div>
                <div className="week-scroll">
                  <table className="week-gantt previsionnel-table">
                    <thead>
                      <tr>
                        <th className="week-gantt-activity">Activité</th>
                        {days.map((key) => (
                          <th key={key}>{fmtShortDayLabel(key)}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {forecastActivities.map(({ id: activityId, label }) => (
                        <tr key={activityId}>
                          <th className="week-gantt-activity">{label}</th>
                          {days.map((key) => {
                            const f = weekForecasts.get(key)?.find((x) => x.activityId === activityId);
                            if (!f) return <td key={key} className="previsionnel-cell">—</td>;
                            const planned = plannedWindowFor(key, activityId);
                            const aligned =
                              planned !== null &&
                              planned.windowStart.slice(0, 5) === f.windowStart &&
                              planned.windowEnd.slice(0, 5) === f.windowEnd;
                            // Deliberately set, but the load has moved since.
                            const drifted =
                              !aligned && (overrides.get(key) ?? []).some((o) => o.activityId === f.activityId);
                            return (
                              <td key={key} className="previsionnel-cell">
                                <span className="previsionnel-window">
                                  {f.windowStart}–{f.windowEnd}
                                </span>
                                <span className="previsionnel-heads">
                                  {f.heads} pers. · {f.aBlancEquivalents.toLocaleString('fr-FR', {
                                    minimumFractionDigits: 1,
                                    maximumFractionDigits: 1,
                                  })} éq.
                                </span>
                                {drifted && (
                                  <span
                                    className="previsionnel-drift"
                                    title="L’horaire de ce jour avait été fixé ; la charge a changé depuis."
                                  >
                                    ⚠ modifié depuis
                                  </span>
                                )}
                                {aligned ? (
                                  <span className="previsionnel-aligned">✓ aligné</span>
                                ) : (
                                  canWriteBoard && (
                                    <button
                                      type="button"
                                      className={`previsionnel-apply${drifted ? ' is-drifted' : ''}`}
                                      disabled={applying === `${key}:${activityId}`}
                                      title="Aligne l’horaire planifié sur le prévisionnel, pour ce jour uniquement"
                                      onClick={() => void applyForecast(key, f)}
                                    >
                                      {applying === `${key}:${activityId}` ? '…' : drifted ? 'Réaligner' : 'Appliquer'}
                                    </button>
                                  )
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {legendTeams.length > 1 && (
              <div className="gantt-legend">
                {legendTeams.map((t) => (
                  <span key={t ?? 'none'} className={`gantt-legend-item ${teamClass(t)}`}>
                    {t ? teamLabel(t) : 'Autres'}
                  </span>
                ))}
              </div>
            )}
            <div className="week-scroll">
              <table className="week-gantt">
                <thead>
                  <tr>
                    <th className="week-gantt-activity">Activité</th>
                    {days.map((key) => {
                      const c = coverages.get(key);
                      return (
                        <th key={key} className={planned(key) ? '' : 'is-unplanned'}>
                          <div>{fmtShortDayLabel(key)}</div>
                          <div className="week-gantt-load">
                            {planned(key) && c?.demandEntered
                              ? `${c.roomsToTurn} ch. · ${c.day?.covers ?? 0} cvts`
                              : planned(key)
                                ? 'charge non saisie'
                                : 'non planifié'}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {sortedActivities.map((a) => (
                      <tr key={a.id}>
                        <th className={`week-gantt-activity ${teamClass(a.team)}`}>
                          {a.label}
                          <span className="week-gantt-window">
                            {a.windowStart.slice(0, 5)}–{a.windowEnd.slice(0, 5)}
                          </span>
                        </th>
                        {days.map((key) => {
                          if (!planned(key)) {
                            return (
                              <td key={key} className="week-cell is-unplanned">
                                —
                              </td>
                            );
                          }
                          const c = coverages.get(key)?.activities.find((x) => x.activity.id === a.id);
                          if (!c) return <td key={key} className="week-cell is-unplanned">—</td>;
                          const isEditing = editing?.activityId === a.id && editing?.day === key;
                          const clockIds = clockIdsForDay(key);
                          return (
                            <td
                              key={key}
                              className={`week-cell is-${c.status}${isEditing ? ' is-editing' : ''}`}
                              title={c.override?.note ?? undefined}
                            >
                              {c.status === 'covered' && (
                                <span className="week-cell-chips">
                                  {[
                                    ...c.assigned.map((id) => ({ id, assigned: true })),
                                    ...c.coveredBy.map((id) => ({ id, assigned: false })),
                                  ].map(({ id, assigned }) => {
                                    const e = employeesById.get(id);
                                    const auto = !assigned && clockIds.has(id);
                                    return (
                                      <button
                                        key={id}
                                        type="button"
                                        className={`gantt-chip is-clickable${assigned ? ' is-assigned' : ''}${auto ? ' is-auto' : ''}`}
                                        style={{ background: chipColors.get(id) }}
                                        title={
                                          e
                                            ? `${e.firstName} ${e.lastName}${assigned ? ' — assigné·e' : auto ? ' — d’après le pointage' : ''} — voir sa semaine`
                                            : id
                                        }
                                        onClick={() => e && setFocusEmployee({ employee: e, day: key })}
                                      >
                                        {assigned ? '★' : auto ? '⚡' : ''}
                                        {e ? chipLabel(e) : '?'}
                                      </button>
                                    );
                                  })}
                                </span>
                              )}
                              {c.status === 'gap' && <span className="week-cell-flag">⚠ à pourvoir</span>}
                              {c.status === 'waived' && <span className="week-cell-muted">dispensé</span>}
                              {c.status === 'noDemand' && <span className="week-cell-muted">·</span>}
                              {c.badge ? (
                                <span className="week-cell-demand" title={c.badge.labelFr}>
                                  {c.badge.text}
                                </span>
                              ) : (
                                c.demand > 1 && <span className="week-cell-demand">{c.demand}</span>
                              )}
                              {c.override && <span className="week-cell-adjusted" title="Horaire ajusté ce jour">⏱</span>}
                              <button
                                type="button"
                                className="week-cell-edit"
                                title="Modifier la couverture"
                                onClick={() => setEditing(isEditing ? null : { activityId: a.id, day: key })}
                              >
                                ✎
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                </tbody>
                <tfoot>
                  <tr>
                    <th className="week-gantt-activity">À pourvoir</th>
                    {days.map((key) => {
                      const gaps = planned(key) ? (coverages.get(key)?.gaps ?? []) : [];
                      return (
                        <td key={key} className="week-cell week-cell-foot">
                          {gaps.length > 0 ? (
                            sentDays.has(key) ? (
                              <span className="board-saved">Diffusé ✓</span>
                            ) : (
                              <button
                                type="button"
                                className="week-broadcast"
                                disabled={busy}
                                onClick={() => void broadcast(key)}
                              >
                                📢 {gaps.length}
                              </button>
                            )
                          ) : (
                            <span className="week-cell-muted">{planned(key) ? '✓' : '—'}</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                </tfoot>
              </table>
            </div>
          </section>

          {editing &&
            (() => {
              const c = coverages.get(editing.day)?.activities.find((x) => x.activity.id === editing.activityId);
              if (!c) return null;
              return (
                <div className="board-override-form week-edit-form">
                  <div className="board-override-title">
                    « {c.activity.label} » — {frDayLabel(editing.day)}
                    <button type="button" className="empweek-close week-edit-close" onClick={() => setEditing(null)} aria-label="Fermer">
                      ×
                    </button>
                  </div>
                  {canAssign && (
                    <AssignEditor
                      activityId={c.activity.id}
                      assigned={c.assigned}
                      employees={employees}
                      qualifications={qualifications}
                      busy={busy}
                      onAssign={(id) => void assignEmployee(c.activity.id, editing.day, id)}
                      onUnassign={(id) => void unassignEmployee(c.activity.id, editing.day, id)}
                    />
                  )}
                  <DeclareEditor
                    declaredIds={c.coveredBy}
                    provenanceFor={(id) => provenanceFor(c.activity.id, editing.day, id)}
                    employees={employees}
                    assignedIds={c.assigned}
                    autoCandidateIds={autoCandidatesFor(c.activity.id, editing.day)}
                    requireVia={!canAssign}
                    busy={busy}
                    onDeclare={(id, via) => void declareOnActivity(c.activity.id, editing.day, id, via)}
                    onUndeclare={(id) => void undeclareOnActivity(c.activity.id, editing.day, id)}
                    onAutoDeclare={(id) => void autoDeclareOnActivity(c.activity.id, editing.day, id)}
                  />
                </div>
              );
            })()}

          {focusEmployee && (
            <EmployeeWeek
              employee={focusEmployee.employee}
              color={chipColors.get(focusEmployee.employee.id)}
              anchorDay={focusEmployee.day}
              activities={activities}
              onClose={() => setFocusEmployee(null)}
            />
          )}
        </>
      )}
    </div>
  );
}

type OverrideByDay = Map<string, ActivityOverride[]>;
